// Shared size scale for form controls (Button, Input, Select, Tabs, DatePicker,
// Combobox, Communications, etc). The goal is that two controls using the same
// `size` look identical when placed side by side: same height, font size,
// horizontal padding, icon size and border radius.
//
// Height stays responsive (smaller on mobile, a notch bigger from `md:` up),
// matching what Button/Input/Tabs already used before this file existed.
export type ControlSize = "xs" | "sm" | "md" | "lg" | "xl";

// "2xs" exists only for compact, non-interactive elements (Badge counters). It
// is not part of ControlSize, so form controls and buttons do not accept it.
export type CompactControlSize = "2xs" | ControlSize;

// "default" is kept only for backward compatibility with old call sites; it is
// always treated as an alias of "md".
export type ControlSizeProp = ControlSize | "default";

export function resolveControlSize(size?: ControlSizeProp | null): ControlSize {
  if (!size || size === "default") return "md";
  return size;
}

interface ControlSizeTokens {
  /** Height classes, responsive. */
  height: string;
  /** Font size classes. */
  text: string;
  /** Horizontal padding. */
  px: string;
  /** Vertical padding (only matters for non-flex leaf elements). */
  py: string;
  /** Icon size, as a [&_svg] selector so it applies to any icon child. */
  icon: string;
  /** Gap between icon/text/spinner. */
  gap: string;
  /** Border radius. Same across sizes so controls line up. */
  radius: string;
}

export const CONTROL_SIZE_TOKENS: Record<
  CompactControlSize,
  ControlSizeTokens
> = {
  "2xs": {
    height: "h-3.5 md:h-4",
    text: "text-[9px] md:text-[10px]",
    px: "px-1.5",
    py: "py-0",
    icon: "[&_svg]:size-3",
    gap: "gap-0.5",
    radius: "rounded-md",
  },
  xs: {
    height: "h-5 md:h-6",
    text: "text-xs",
    px: "px-2",
    py: "py-0.5",
    icon: "[&_svg]:size-3.5",
    gap: "gap-1",
    radius: "rounded-md",
  },
  sm: {
    height: "h-6 md:h-7",
    text: "text-sm",
    px: "px-2.5",
    py: "py-1",
    icon: "[&_svg]:size-3.5",
    gap: "gap-1",
    radius: "rounded-md",
  },
  md: {
    height: "h-7 md:h-8",
    text: "text-sm",
    px: "px-3",
    py: "py-1",
    icon: "[&_svg]:size-4",
    gap: "gap-1.5",
    radius: "rounded-md",
  },
  lg: {
    height: "h-8 md:h-9",
    text: "text-base",
    px: "px-4",
    py: "py-1.5",
    icon: "[&_svg]:size-4",
    gap: "gap-2",
    radius: "rounded-md",
  },
  xl: {
    height: "h-9 md:h-10",
    text: "text-base",
    px: "px-5",
    py: "py-2",
    icon: "[&_svg]:size-5",
    gap: "gap-2",
    radius: "rounded-md",
  },
};

/** Full class string (height + text + px + py + radius) for a control's own box. */
export function controlSizeClasses(size?: ControlSizeProp | null): string {
  const t = CONTROL_SIZE_TOKENS[resolveControlSize(size)];
  return `${t.height} ${t.text} ${t.px} ${t.py} ${t.radius}`;
}

/** Just the icon-size class, to apply on a wrapper around an icon. */
export function iconSizeClass(size?: ControlSizeProp | null): string {
  return CONTROL_SIZE_TOKENS[resolveControlSize(size)].icon;
}
